import { t } from '../../i18n';
import { CustomerDetail } from '../../types/customer';
import { downloadSpreadsheetMl, isoDateOnly, type ExcelCell } from '../../utils/exportExcelXml';
import { formatDateTime, formatMoney } from '../../utils/format';
import { escapeHtml, printHtmlDocument } from '../../utils/printDocument';

export function moneyCell(value: string | number, style = 'Money'): ExcelCell {
  return { type: 'number', value: Number(value), style };
}

export function dateCell(value: string | null | undefined): ExcelCell {
  const iso = isoDateOnly(value);
  if (!iso) return { type: 'empty' };
  return { type: 'date', value: iso, style: 'Date' };
}

export function emptyCells(count: number, style?: string): ExcelCell[] {
  return Array.from({ length: count }, () => ({ type: 'empty', style }));
}

export function chunkPages<T>(items: T[], firstSize: number, nextSize: number): T[][] {
  if (items.length === 0) return [[]];
  const pages: T[][] = [items.slice(0, firstSize)];
  for (let index = firstSize; index < items.length; index += nextSize) {
    pages.push(items.slice(index, index + nextSize));
  }
  return pages;
}

export function customerVatContact(customer: CustomerDetail): {
  vat: string;
  contact: string;
} {
  return {
    vat: customer.vatNumber
      ? `${t().common.vatPrefix}: ${customer.vatNumber}`
      : t().common.noVat,
    contact: [customer.phone, customer.email].filter(Boolean).join(' · '),
  };
}

export function excelLeadRows(
  customer: CustomerDetail,
  title: string,
  columnCount: number,
): ExcelCell[][] {
  const merge = Math.max(0, columnCount - 1);
  const nameMerge = Math.max(0, columnCount - 2);
  const { vat, contact } = customerVatContact(customer);
  const labels = t().customers;
  return [
    [{ type: 'string', value: 'BalanceFlow', style: 'Title', mergeAcross: merge }],
    [{ type: 'string', value: title, style: 'Subtitle', mergeAcross: merge }],
    emptyCells(1),
    [
      { type: 'string', value: `${labels.name}:`, style: 'Label' },
      { type: 'string', value: customer.name, mergeAcross: nameMerge },
    ],
    [
      { type: 'string', value: labels.vatNumber, style: 'Label' },
      { type: 'string', value: vat, mergeAcross: nameMerge },
    ],
    [
      { type: 'string', value: labels.phone, style: 'Label' },
      { type: 'string', value: contact || '—', mergeAcross: nameMerge },
    ],
  ];
}

export function excelHeaderRow(
  headers: string[],
  isNumeric: (index: number) => boolean,
): ExcelCell[] {
  return headers.map((header, index) => ({
    type: 'string' as const,
    value: header,
    style: isNumeric(index) ? 'ColHeaderNum' : 'ColHeader',
  }));
}

export function excelSubtotal(
  column: number,
  firstRow: number,
  lastRow: number,
): ExcelCell {
  if (lastRow < firstRow) return moneyCell(0, 'MoneyBold');
  return {
    type: 'formula',
    formula: `=SUBTOTAL(109,R${firstRow}C${column}:R${lastRow}C${column})`,
    style: 'MoneyBold',
  };
}

export function downloadCustomerExcel(input: {
  customer: CustomerDetail;
  filename: string;
  sheetTitle: string;
  columnWidths: number[];
  columnCount: number;
  headers: string[];
  isNumericHeader: (index: number) => boolean;
  summaryRows?: ExcelCell[][];
  dataRows: ExcelCell[][];
  totalRow: ExcelCell[];
}): void {
  const labels = t().customers;
  const printedAt = formatDateTime(new Date());
  const merge = Math.max(0, input.columnCount - 1);

  const rows: ExcelCell[][] = [
    ...excelLeadRows(input.customer, input.sheetTitle, input.columnCount),
    emptyCells(1),
    ...(input.summaryRows ?? []),
    ...(input.summaryRows?.length ? [emptyCells(1)] : []),
    excelHeaderRow(input.headers, input.isNumericHeader),
  ];
  const headerRow = rows.length;
  const lastDataRow =
    input.dataRows.length > 0 ? headerRow + input.dataRows.length : headerRow;

  rows.push(...input.dataRows);
  rows.push(input.totalRow);
  rows.push(emptyCells(1));
  rows.push([
    {
      type: 'string',
      value: `${labels.printedAt}: ${printedAt}`,
      style: 'Footer',
      mergeAcross: merge,
    },
  ]);

  downloadSpreadsheetMl(input.filename, {
    sheetName: input.sheetTitle,
    columnWidths: input.columnWidths,
    rows,
    freezeRows: headerRow,
    autoFilter:
      input.dataRows.length > 0
        ? {
            headerRow,
            lastDataRow,
            columns: input.columnCount,
          }
        : undefined,
    headerLeft: `BalanceFlow — ${input.sheetTitle}`,
    footerLeft: `${labels.printedAt}: ${printedAt}`,
    footerRight: `${labels.page} &P ${labels.pageOf} &N`,
  });
}

/** 1-based header row after lead (6) + blank + optional summary + blank. */
export function excelHeaderRowNumber(summaryRowCount: number): number {
  return 7 + (summaryRowCount > 0 ? summaryRowCount + 1 : 0) + 1;
}

export function htmlSummaryCard(
  titleText: string,
  invoice: string,
  cash: string,
  total: string,
): string {
  return `
    <div class="summary-card">
      <div class="label">${escapeHtml(titleText)}</div>
      <div class="row"><span>${escapeHtml(t().financial.invoice)}</span><span>${escapeHtml(formatMoney(invoice))}</span></div>
      <div class="row"><span>${escapeHtml(t().financial.cash)}</span><span>${escapeHtml(formatMoney(cash))}</span></div>
      <div class="row"><span>${escapeHtml(t().financial.grandTotal)}</span><span>${escapeHtml(formatMoney(total))}</span></div>
    </div>`;
}

export function htmlCustomerIntro(
  customer: CustomerDetail,
  title: string,
  summaryHtml: string,
): string {
  const { vat, contact } = customerVatContact(customer);
  return `
    <h1>BalanceFlow</h1>
    <div class="subtitle">${escapeHtml(title)}</div>
    <div class="meta">
      <div><strong>${escapeHtml(customer.name)}</strong></div>
      <div>${escapeHtml(vat)}${contact ? ` · ${escapeHtml(contact)}` : ''}</div>
    </div>
    ${summaryHtml}`;
}

export function htmlSheetFooter(
  printedAt: string,
  page: number,
  pageCount: number,
): string {
  const labels = t().customers;
  return `
    <div class="sheet-footer">
      <span>${escapeHtml(labels.printedAt)}: ${escapeHtml(printedAt)}</span>
      <span>${escapeHtml(labels.page)} ${page} ${escapeHtml(labels.pageOf)} ${pageCount}</span>
    </div>`;
}

export function htmlHeaderRow(
  headers: string[],
  isNumeric: (index: number) => boolean,
): string {
  return `<tr>${headers
    .map((header, index) => {
      const numeric = isNumeric(index);
      return `<th class="${numeric ? 'num' : ''}">${escapeHtml(header)}</th>`;
    })
    .join('')}</tr>`;
}

export function htmlDataRow(
  cells: string[],
  isNumeric: (index: number) => boolean,
): string {
  return `<tr>${cells
    .map((cell, index) => {
      const numeric = isNumeric(index);
      return `<td class="${numeric ? 'num' : ''}">${escapeHtml(cell)}</td>`;
    })
    .join('')}</tr>`;
}

export function printCustomerPdf<T>(input: {
  customer: CustomerDetail;
  title: string;
  summaryHtml: string;
  headers: string[];
  isNumeric: (index: number) => boolean;
  rowHtml: (item: T) => string;
  items: T[];
  totalsRowHtml: string;
  firstPageRows?: number;
  nextPageRows?: number;
}): void {
  const printedAt = formatDateTime(new Date());
  const headerRow = htmlHeaderRow(input.headers, input.isNumeric);
  const title = `${input.title} — ${input.customer.name}`;
  const pages = chunkPages(
    input.items,
    input.firstPageRows ?? 16,
    input.nextPageRows ?? 22,
  );
  const pageCount = pages.length;
  const intro = htmlCustomerIntro(input.customer, input.title, input.summaryHtml);

  const sheets = pages
    .map((pageItems, index) => {
      const page = index + 1;
      const isFirst = index === 0;
      const isLast = index === pages.length - 1;
      const heading = isFirst
        ? intro
        : `<div class="running-head">${escapeHtml(title)}</div>`;

      return `
        <section class="sheet">
          ${heading}
          <table>
            <thead>${headerRow}</thead>
            <tbody>${pageItems.map(input.rowHtml).join('')}</tbody>
            ${isLast ? `<tfoot>${input.totalsRowHtml}</tfoot>` : ''}
          </table>
          ${htmlSheetFooter(printedAt, page, pageCount)}
        </section>`;
    })
    .join('');

  printHtmlDocument(title, sheets);
}

export { escapeHtml, formatMoney };
